/**
 * NarrativeCards — content for cross-stage / pre-boss story cards.
 *
 * These are NOT the per-stage intro modals (those live in stage JSON
 * `educational.intro`). These are the *transitions* between worlds and
 * the lead-in to the boss fight, sourced from the client's narrative
 * brief and templated with the player's callsign at render time.
 *
 * Bridge cards fire after the player clicks NEXT STAGE on the
 * StageClearScene of a world's last stage. The boss intro fires once
 * at the start of BossScene before the existing warning cinematic.
 */

export interface NarrativeCard {
  /** Big title bar shown above the body (HELMEPA mission slug). */
  title: string;
  /** Multi-paragraph body. */
  body: string;
  /** CTA button label, e.g. "TO THE SHORE". */
  cta: string;
  /** Optional preloaded texture key for a hero image (full-bleed, 9:16). */
  image?: string;
}

/**
 * Bridges fire AFTER the StageClearScene of the named "from" stage,
 * BEFORE the next world's first stage loads. Keys are the source stage
 * number (the stage the player just completed).
 */
export const WORLD_BRIDGES: Record<number, NarrativeCard> = {
  // After stage 2 → world 2 begins (THE SHORE)
  2: {
    title: 'Next Mission',
    body:
      'The sea is more than the deep. Above the waves,\n' +
      'the shore tells another story.\n' +
      'Ready for your next mission?',
    cta: 'TO THE SHORE',
  },
  // After stage 4 → world 3 begins (ON BOARD)
  4: {
    title: 'Next Mission',
    body:
      'You have saved the deep. You have saved the shore. Now the biggest challenge of all. Out at sea, the storm is rising.',
    cta: 'BOARD THE SHIP',
  },
  // After stage 6 → boss (THE HYDRA)
  6: {
    title: 'Next Mission',
    body:
      'One final threat remains. Every bag, every cigarette butt, every storm: they all come together in one final challenge. Now it stands before you.',
    cta: 'FACE THE HYDRA',
  },
};

/**
 * Boss intro card — fires at the start of BossScene, before the existing
 * warning + name-banner cinematic. Image is the dramatic Hydra portrait
 * generated for /assets/ui/education/boss_intro.png.
 */
export const BOSS_INTRO: NarrativeCard = {
  title: "LEGEND'S TRIAL: THE POLLUTION HYDRA",
  body:
    'You saved the deep. You saved the shore. You weathered the storm.\n\n' +
    'But the waste you fought has a face. It has been growing in the deepest trench, fed by every bag, every butt, every storm we made worse.\n\n' +
    'The Pollution Hydra has risen. Cut one head. Two grow back. But the trident remembers. Show it what you have learned.',
  cta: 'STRIKE THE HYDRA',
  image: 'boss_intro',
};
